import axios from 'axios';

// Use VITE_API_URL for development, VITE_API_BUILD_URL for production build
const API_URL = import.meta.env.MODE === 'development'
    ? (import.meta.env.VITE_API_URL || 'http://localhost:5010')
    : (import.meta.env.VITE_API_BUILD_URL || '/');

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Callback for 401 errors
let on401Callback: (() => void) | null = null;

export const setOn401Callback = (callback: () => void) => {
    on401Callback = callback;
};

// Add a request interceptor to add the auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        const loginLogId = localStorage.getItem('loginLogId');
        if (loginLogId) {
            config.headers['x-login-log-id'] = loginLogId;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle global errors
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        // Dynamic import to avoid circular dependencies if any, though direct import is fine here usually.
        // But better to import at top level if possible. 
        // Let's use top level import in the next step or just use the global object if I imported it.
        // Wait, I need to import SnackbarUtils at the top.
        // For now, I will add the import at the top in a separate edit or include it here if I can't.
        // I'll assume I can add the import.

        const { default: SnackbarUtils } = await import('../utils/SnackbarUtils');

        if (error.response) {
            const { status } = error.response;
            switch (status) {
                case 400:
                    SnackbarUtils.error(error.response.data.message || 'Requête invalide');
                    break;
                case 401:
                    if (on401Callback) {
                        on401Callback();
                    }
                    SnackbarUtils.error('Session expirée ou non autorisée');
                    break;
                case 403:
                    SnackbarUtils.error('Accès refusé');
                    break;
                case 404:
                    SnackbarUtils.error('Ressource introuvable');
                    break;
                case 500:
                    SnackbarUtils.error('Erreur serveur interne');
                    break;
                default:
                    SnackbarUtils.error('Une erreur est survenue');
            }
        } else if (error.request) {
            SnackbarUtils.error('Erreur de connexion au serveur');
        } else {
            SnackbarUtils.error('Erreur inattendue');
        }
        return Promise.reject(error);
    }
);

// Email API functions
export const getUserEmails = async (userId: string, params?: { type?: string; status?: string; page?: number; limit?: number }) => {
    const response = await api.get(`/api/emails/user/${userId}`, { params });
    return response.data;
};

export const getEmail = async (emailId: string) => {
    const response = await api.get(`/api/emails/${emailId}`);
    return response.data;
};

export default api;
